import React, { useState, useEffect, useMemo } from "react";
import { getAppointmentOwnerName } from "../../utils/appointmentOwner";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import i18n from "../../../../i18n";
import styles from "../../appointmentsPage.module.css";
import {
  IAppointment,
  IAppointmentStats,
} from "../../types/appointments.types";
import { useAppointmentsContext } from "../../context/AppointmentsContext";
import { useAppSelector } from "../../../../services/hooks";
import { adminSelector } from "../../../../services/selectors/user.selectors";
import AppointmentStats from "../AppointmentStats/AppointmentStats";
import AppointmentDetailsModal from "../AppointmentDetailsModal/AppointmentDetailsModal";
import AppointmentBadges from "../ActionReportBadge/AppointmentBadges";
import IconsLegend from "../IconsLegend/IconsLegend";
import DayAppointmentsModal from "../DayAppointmentsModal/DayAppointmentsModal";
import CreateAppointmentModal from "../CreateAppointmentModal/CreateAppointmentModal";
import MoveConfirmationModal from "../MoveConfirmationModal/MoveConfirmationModal";
import {
  getAppointmentColors,
  truncateText,
} from "../../utils/fundingSourceColors";
import {
  getInitialDateForView,
  saveDateForView,
  parseDateStringLocal,
  toDateStringLocal,
} from "../../utils/calendarStorage";
import { usePatchAppointmentMutation } from "../../../../services/api/utils.api";
import {
  calculateVerticalOffset,
  calculateHeight,
  detectOverlaps,
  calculateHorizontalLayout,
  getContainingHourSlot,
  timeToMinutes,
  calculateEndTime,
  AppointmentPosition,
} from "../../utils/timeUtils";
import ExportTableButtons from "../../../../components/exportTableButtons/ExportTableButtons";
import {
  buildAppointmentsExport,
  buildAdminFilterSummary,
} from "../../utils/buildAppointmentsExport";
import { useCalendarColumns } from "../../hooks/useCalendarColumns";

// Get locale based on current language
const getLocale = () => (i18n.language === "he" ? "he-IL" : "en-US");

/**
 * Daily View Component
 *
 * Displays appointments for a single day in a grid layout organized by therapist columns.
 * Each therapist has their own column, with hourly time slots on the side.
 * Uses real API data with smart month-based caching.
 */
const DailyView: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState(() =>
    getInitialDateForView("daily"),
  );
  const [tempSelectedDate, setTempSelectedDate] = useState<string | null>(null);
  const [selectedAppointment, setSelectedAppointment] =
    useState<IAppointment | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDayModalOpen, setIsDayModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<{
    date: string;
    therapistId?: number;
    appointments: IAppointment[];
  } | null>(null);

  // Create Appointment State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalDate, setCreateModalDate] = useState("");
  const [createModalTime, setCreateModalTime] = useState("");
  const [createModalTherapistId, setCreateModalTherapistId] = useState<
    number | undefined
  >(undefined);

  // Expandable Hours State
  const [expandedHours, setExpandedHours] = useState<Record<string, boolean>>(
    {},
  );
  // Tooltip State
  const [tooltip, setTooltip] = useState<{
    text: string;
    x: number;
    y: number;
  } | null>(null);

  // Drag & Drop State
  const [draggedAppointment, setDraggedAppointment] =
    useState<IAppointment | null>(null);
  const [draggedFromSlot, setDraggedFromSlot] = useState<{
    therapistId: number;
    time: string;
  } | null>(null);
  const [dragOverSlot, setDragOverSlot] = useState<{
    therapistId: number;
    time: string;
  } | null>(null);
  const [showMoveConfirmation, setShowMoveConfirmation] = useState(false);
  const [pendingMove, setPendingMove] = useState<{
    appointment: IAppointment;
    fromTherapistId: number;
    fromTime: string;
    fromEndTime: string;
    toTherapistId: number;
    toTherapistGuid?: string;
    toTherapistName: string;
    toTime: string;
    toEndTime: string;
    warning?: string;
  } | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState<"success" | "error">("success");

  const admin = useAppSelector(adminSelector);

  // API hooks
  const [patchAppointment] = usePatchAppointmentMutation();

  // Use shared appointments context (data is cached and shared across all views)
  const {
    ensureMonthLoaded,
    reloadMonth,
    filterAppointments,
    therapistsList,
    deviceColumns,
    columnMode,
    therapistAvailability,
    companiesOptions,
    fundingSourcesList,
    filters,
    isLoading,
    isFullscreen,
    showFiltersInFullscreen,
  } = useAppointmentsContext();

  // Load current month on mount
  useEffect(() => {
    if (admin.companyId) {
      ensureMonthLoaded(selectedDate);
    }
  }, [admin.companyId, selectedDate, ensureMonthLoaded]);

  // Persist date when it changes (for navigation back)
  useEffect(() => {
    saveDateForView("daily", selectedDate);
  }, [selectedDate]);

  // Filter appointments for selected day
  const { dayStart, dayEnd } = useMemo(() => {
    const start = new Date(selectedDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(selectedDate);
    end.setHours(23, 59, 59, 999);
    return { dayStart: start, dayEnd: end };
  }, [selectedDate]);

  const dayAppointments = useMemo(() => {
    return filterAppointments({
      dateRange: { start: dayStart, end: dayEnd },
      fakeCompanyIds: filters.fakeCompanyIds,
      therapistIds: filters.therapistIds,
      fundingSourceIds: filters.fundingSourceIds,
      attendanceStatuses: filters.attendanceStatuses,
      patientSearch: filters.patientSearch,
      includeOtherEventsForAdminId: Number(admin.id),
    });
  }, [
    filterAppointments,
    dayStart,
    dayEnd,
    filters.fakeCompanyIds,
    filters.therapistIds,
    filters.fundingSourceIds,
    filters.attendanceStatuses,
    filters.patientSearch,
    admin.id,
  ]);

  // Column axis, shared with the weekly and monthly boards. Daily shows every machine when no
  // device is picked: there are only a handful and a free one is what you are looking for.
  const { columns: displayedTherapists, isColumnSplit, isDeviceMode, matchesColumn, matchesAxis } =
    useCalendarColumns({
      appointments: dayAppointments,
      filters,
      splitDevicesWhenNoSelection: true,
    });

  // Narrowed to the active axis at the source, so everything downstream — layouts, slots, totals
  // and the export — inherits it without each having to remember.
  const filteredAppointments = useMemo(
    () => dayAppointments.filter(matchesAxis),
    [dayAppointments, matchesAxis],
  );

  // Generate hourly time slots from 07:00 to 22:00
  const hourlySlots = useMemo(() => {
    const slots: string[] = [];
    for (let hour = 7; hour <= 22; hour++) {
      slots.push(`${hour.toString().padStart(2, "0")}:00`);
    }
    return slots;
  }, []);

  // Get 15-minute sub-slots for a specific hour (e.g., "08" => ["08:15", "08:30", "08:45"])
  const getSubSlots = (hour: string): string[] => {
    return ["15", "30", "45"].map((minute) => `${hour}:${minute}`);
  };

  // Toggle individual hour expansion
  const toggleHour = (hour: string) => {
    setExpandedHours((prev) => ({
      ...prev,
      [hour]: !prev[hour],
    }));
  };

  // Toggle all hours at once (expand/collapse all)
  const toggleAllHours = () => {
    const allExpanded = hourlySlots.every(
      (s) => expandedHours[s.split(":")[0]],
    );
    if (allExpanded) {
      setExpandedHours({});
    } else {
      const newState: Record<string, boolean> = {};
      hourlySlots.forEach((s) => {
        newState[s.split(":")[0]] = true;
      });
      setExpandedHours(newState);
    }
  };

  // Build tooltip text for an appointment (same logic as WeeklyView)
  const getAppointmentTooltip = (apt: IAppointment): string => {
    const isGoogleEvent = apt.source === "google_calendar";
    const isOther = !apt.usersId && !apt.treatmentGroupId && !isGoogleEvent;
    let tip = "";
    if (apt.time) {
      tip += `${apt.time}`;
      if (apt.endTime) {
        tip += ` - ${apt.endTime}`;
      } else if (apt.duration) {
        tip += ` - ${calculateEndTime(apt.time, apt.duration)}`;
      }
      tip += " | ";
    }
    if (isGoogleEvent) {
      tip += `📅 ${apt.summary || apt.description || "Google Calendar"}`;
    } else if (isOther) {
      tip += `📝 ${apt.comments || t("OTHER") || "אחר"}`;
    } else if (apt.treatmentGroupName && !apt.patientName) {
      tip += apt.treatmentGroupName;
    } else {
      tip += apt.patientName || "";
    }
    if (!isGoogleEvent && !isOther && getAppointmentOwnerName(apt)) {
      tip += ` | ${getAppointmentOwnerName(apt)}`;
    }
    return tip;
  };

  // Get appointments for a specific therapist and time slot
  const getAppointmentsForSlot = (
    time: string,
    therapistId?: number,
  ): IAppointment[] => {
    const [slotHourStr, slotMinuteStr] = time.split(":");
    const slotHour = parseInt(slotHourStr, 10);
    const slotMinute = parseInt(slotMinuteStr, 10);

    // Check if this hour is expanded
    const isExpanded = expandedHours[slotHourStr];

    return filteredAppointments.filter((apt) => {
      // Filter by therapist if provided
      if (therapistId !== undefined) {
        if (therapistId === -1) {
          const isGoogleEvent = apt.source === "google_calendar";
          const isOther =
            (!apt.patientName && !apt.treatmentGroupName && !apt.mediaId) ||
            isGoogleEvent;
          if (!(isOther && apt.therapistsId === Number(admin.id))) return false;
        } else if (!matchesColumn(apt, therapistId)) {
          return false;
        }
      }

      const aptTime = apt.time;
      if (!aptTime) {
        return slotHour === 7 && slotMinute === 0;
      }

      const [aptHourStr, aptMinuteStr] = aptTime.split(":");
      const aptHour = parseInt(aptHourStr, 10);
      const aptMinute = parseInt(aptMinuteStr || "0", 10);

      // Special handling for appointments before 07:00
      if (aptHour < 7) {
        return slotHour === 7 && slotMinute === 0;
      }

      // Special handling for appointments after 22:00
      if (aptHour >= 23) {
        if (isExpanded && expandedHours["22"]) {
          return slotHour === 22 && slotMinute === 45;
        } else {
          return slotHour === 22 && slotMinute === 0;
        }
      }

      // If this is a main hour slot (xx:00)
      if (slotMinute === 0) {
        if (isExpanded) {
          // When expanded, main slot shows only 00-14 minute appointments
          return aptHour === slotHour && aptMinute < 15;
        } else {
          // When collapsed, main slot shows ALL appointments in that hour
          return aptHour === slotHour;
        }
      } else {
        // This is a sub-slot (xx:15, xx:30, xx:45)
        // Match appointment to the 15-minute slot it falls into
        const aptTotalMinutes = aptHour * 60 + aptMinute;
        const slotTotalMinutes = slotHour * 60 + slotMinute;
        const aptSlotMinutes = Math.floor(aptTotalMinutes / 15) * 15;
        return aptSlotMinutes === slotTotalMinutes;
      }
    });
  };

  const isSlotAvailable = (date: Date, time: string, therapistId?: number) => {
    if (!therapistId || therapistId === -1) return false;

    const dayId = date.getDay() + 1;

    return therapistAvailability.some((avail) => {
      const availTherapistId =
        avail.therapistsId || (avail as any).TherapistsId;
      if (availTherapistId !== therapistId) return false;

      const availDayId = avail.dayId || (avail as any).DayId;
      if (availDayId !== dayId) return false;

      const availStatusId = avail.statusId ?? (avail as any).StatusId;
      const isAvailable = avail.isAvailable ?? (avail as any).IsAvailable;
      if (!isAvailable || availStatusId !== 1) return false;

      const slotTimeMin = timeToMinutes(time);
      const availStart = avail.startTime || (avail as any).StartTime;
      const availEnd = avail.endTime || (avail as any).EndTime;
      const availStartMin = timeToMinutes(availStart);
      const availEndMin = timeToMinutes(availEnd);

      return slotTimeMin >= availStartMin && slotTimeMin < availEndMin;
    });
  };

  const getTherapistHours = (date: Date, therapistId: number) => {
    if (!therapistId || therapistId === -1) return [];

    const dayId = date.getDay() + 1;

    const hours = therapistAvailability
      .filter((avail) => {
        const availTherapistId =
          avail.therapistsId || (avail as any).TherapistsId;
        if (availTherapistId !== therapistId) return false;

        const availDayId = avail.dayId || (avail as any).DayId;
        if (availDayId !== dayId) return false;

        const availStatusId = avail.statusId ?? (avail as any).StatusId;
        const isAvailable = avail.isAvailable ?? (avail as any).IsAvailable;
        if (!isAvailable || availStatusId !== 1) return false;
        return true;
      })
      .map((avail) => {
        const availStart = avail.startTime || (avail as any).StartTime;
        const availEnd = avail.endTime || (avail as any).EndTime;
        if (!availStart || !availEnd) return null;
        return `${availStart.substring(0, 5)} - ${availEnd.substring(0, 5)}`;
      })
      .filter(Boolean) as string[];

    return Array.from(new Set(hours)).sort();
  };

  // Calculate stats
  const stats: IAppointmentStats = useMemo(() => {
    const total = filteredAppointments.length;
    const activeTherapists = new Set(
      filteredAppointments.map((a) => a.therapistsId),
    ).size;

    return {
      totalToday: total,
      confirmed: total, // All active appointments are considered confirmed
      pending: 0,
      cancelled: 0,
      activeTherapists,
      totalTherapists: therapistsList.filter((t) => t.isActive).length,
    };
  }, [filteredAppointments, therapistsList]);

  // Export payload (PDF/Excel) for the currently displayed, filtered day.
  const exportProps = useMemo(
    () =>
      buildAppointmentsExport(filteredAppointments, {
        viewType: "daily",
        rangeLabel: selectedDate.toLocaleDateString(getLocale(), {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
        }),
        filterSummary: buildAdminFilterSummary(filters, {
          therapistsList,
          companiesOptions,
          fundingSourcesList,
        }),
      }),
    [
      filteredAppointments,
      selectedDate,
      filters,
      therapistsList,
      companiesOptions,
      fundingSourcesList,
    ],
  );

  // Pre-calculate proportional layout positions for filtered therapist view
  const appointmentLayouts = useMemo(() => {
    if (!isColumnSplit)
      return new Map<number, Map<string, AppointmentPosition[]>>();
    const layouts = new Map<number, Map<string, AppointmentPosition[]>>();
    for (const therapist of displayedTherapists) {
      const isVirtualCalendar = therapist.id === -1;
      const therapistAppts = filteredAppointments.filter((apt) => {
        if (isVirtualCalendar) {
          const isGoogleEvent = apt.source === "google_calendar";
          const isOther =
            (!apt.patientName && !apt.treatmentGroupName && !apt.mediaId) ||
            isGoogleEvent;
          return isOther && apt.therapistsId === Number(admin.id);
        }
        return matchesColumn(apt, therapist.id);
      });
      const slotLayouts = new Map<string, AppointmentPosition[]>();
      for (const hourSlot of hourlySlots) {
        const slotAppts = therapistAppts.filter(
          (apt) => getContainingHourSlot(apt.time || "07:00") === hourSlot,
        );
        if (slotAppts.length === 0) continue;
        const overlapGroups = detectOverlaps(slotAppts);
        const positions: AppointmentPosition[] = [];
        for (const group of overlapGroups) {
          positions.push(...calculateHorizontalLayout(group));
        }
        slotLayouts.set(hourSlot, positions);
      }
      layouts.set(therapist.id, slotLayouts);
    }
    return layouts;
  }, [
    isColumnSplit,
    matchesColumn,
    displayedTherapists,
    filteredAppointments,
    hourlySlots,
    admin.id,
  ]);

  const handleAppointmentClick = (appointment: IAppointment) => {
    setSelectedAppointment(appointment);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAppointment(null);
  };

  const handleAppointmentCancelled = () => {
    reloadMonth(selectedDate);
  };

  // Handle "More Appointments" click
  const handleMoreAppointmentsClick = (
    e: React.MouseEvent,
    appointments: IAppointment[],
    therapistId?: number,
  ) => {
    e.stopPropagation();
    // Use the selected date formatted as YYYY-MM-DD
    const dateStr = toDateStringLocal(selectedDate);

    setSelectedDay({
      date: dateStr,
      therapistId,
      appointments,
    });
    setIsDayModalOpen(true);
  };

  const handleCloseDayModal = () => {
    setIsDayModalOpen(false);
    setSelectedDay(null);
  };

  const handleDayAppointmentClick = (appointment: IAppointment) => {
    setIsDayModalOpen(false);
    setSelectedAppointment(appointment);
    setIsModalOpen(true);
  };

  // Drag & Drop Handlers
  const handleDragStart = (
    e: React.DragEvent,
    appointment: IAppointment,
    therapistId: number,
    time: string,
  ) => {
    // Device mode has no drag. Guarded here rather than only through the draggable attribute, so
    // no render path can quietly reopen it: moving a patient between machines is a different
    // action from moving them between therapists, and it would skip the availability check.
    if (isDeviceMode) return;

    setDraggedAppointment(appointment);
    setDraggedFromSlot({ therapistId, time });
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    setDraggedAppointment(null);
    setDraggedFromSlot(null);
    setDragOverSlot(null);
  };

  const handleDragOver = (
    e: React.DragEvent,
    therapistId: number,
    time: string,
  ) => {
    if (isDeviceMode) return;

    e.preventDefault();
    e.dataTransfer.dropEffect = "move";

    if (!draggedAppointment) return;

    setDragOverSlot({ therapistId, time });
  };

  const handleDragLeave = () => {
    setDragOverSlot(null);
  };

  const handleDrop = (
    e: React.DragEvent,
    toTherapistId: number,
    toTime: string,
  ) => {
    if (isDeviceMode) return;

    e.preventDefault();

    if (!draggedAppointment || !draggedFromSlot) return;

    const { therapistId: fromTherapistId, time: fromTime } = draggedFromSlot;

    // Don't do anything if dropped on the same slot
    if (fromTherapistId === toTherapistId && fromTime === toTime) {
      handleDragEnd();
      return;
    }

    // Check if there are already appointments in the target slot
    const targetSlotAppointments = getAppointmentsForSlot(
      toTime,
      toTherapistId,
    );
    const hasConflict = targetSlotAppointments.length > 0;

    const warning = hasConflict ? t("APPOINTMENTS_THERAPIST_BUSY") : undefined;

    // Calculate end times
    const calculateEndTime = (
      startTime: string,
      durationMinutes: number,
    ): string => {
      const [hours, minutes] = startTime.split(":").map(Number);
      const totalMinutes = hours * 60 + minutes + durationMinutes;
      const endHours = Math.floor(totalMinutes / 60) % 24;
      const endMinutes = totalMinutes % 60;
      return `${endHours.toString().padStart(2, "0")}:${endMinutes.toString().padStart(2, "0")}`;
    };

    const duration = draggedAppointment.duration || 30;
    const fromEndTime = calculateEndTime(fromTime, duration);
    const toEndTime = calculateEndTime(toTime, duration);

    const toTherapist = therapistsList.find((t) => t.id === toTherapistId);

    // Set pending move and show confirmation
    setPendingMove({
      appointment: draggedAppointment,
      fromTherapistId,
      fromTime,
      fromEndTime,
      toTherapistId,
      toTherapistGuid: toTherapist?.guid,
      toTherapistName: toTherapist?.name || draggedAppointment.therapistName,
      toTime,
      toEndTime,
      warning,
    });
    setShowMoveConfirmation(true);

    handleDragEnd();
  };

  const handleCancelMove = () => {
    setShowMoveConfirmation(false);
    setPendingMove(null);
  };

  const showToastMessage = (message: string, type: "success" | "error") => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleConfirmMove = async (
    newDate: string,
    newTime: string,
    newEndTime: string,
    newTherapistId: number,
  ) => {
    if (!pendingMove) return;

    const { appointment } = pendingMove;

    try {
      // Find therapist GUID
      const therapist = therapistsList.find((t) => t.id === newTherapistId);

      // Update appointment via API
      await patchAppointment({
        id: appointment.id,
        startTime: newTime !== appointment.time ? newTime : undefined,
        endTime: newEndTime !== appointment.endTime ? newEndTime : undefined,
        therapistsGuid:
          therapist?.guid !== appointment.therapistsGuid
            ? therapist?.guid
            : undefined,
        therapistsId:
          newTherapistId !== appointment.therapistsId
            ? newTherapistId
            : undefined,
      }).unwrap();

      // Show toast
      showToastMessage(
        t("APPOINTMENTS_MOVED_SUCCESS") || "Appointment moved successfully",
        "success",
      );

      // Cleanup
      setShowMoveConfirmation(false);
      setPendingMove(null);

      // Reload data
      reloadMonth(selectedDate);
    } catch (error) {
      console.error("Failed to move appointment:", error);
      showToastMessage(
        t("APPOINTMENTS_MOVE_FAILED") || "Failed to move appointment",
        "error",
      );
    }
  };

  const getSlotClassName = (time: string, therapistId?: number): string => {
    const baseClass = styles.dailyTimeSlot;

    if (
      dragOverSlot &&
      dragOverSlot.time === time &&
      (therapistId === undefined || dragOverSlot.therapistId === therapistId)
    ) {
      return `${baseClass} ${styles.dragOver}`;
    }

    return baseClass;
  };

  const handleDateChange = (direction: "prev" | "next") => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + (direction === "next" ? 1 : -1));
    setSelectedDate(newDate);
    // Ensure new month is loaded if we crossed month boundary
    ensureMonthLoaded(newDate);
  };

  const handleOpenCreateModal = (time: string, therapistId?: number) => {
    // Format date as YYYY-MM-DD for the modal
    const dateStr = toDateStringLocal(selectedDate);

    setCreateModalDate(dateStr);
    setCreateModalTime(time);
    setCreateModalTherapistId(therapistId);
    setShowCreateModal(true);
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    showToastMessage(
      t("MEETING_CREATED_SUCCESSFULLY") || "Appointment created successfully",
      "success",
    );
    reloadMonth(selectedDate);
  };

  const formatDate = (date: Date): string => {
    const days = [
      t("APPOINTMENTS_DAY_SUNDAY"),
      t("APPOINTMENTS_DAY_MONDAY"),
      t("APPOINTMENTS_DAY_TUESDAY"),
      t("APPOINTMENTS_DAY_WEDNESDAY"),
      t("APPOINTMENTS_DAY_THURSDAY"),
      t("APPOINTMENTS_DAY_FRIDAY"),
      t("APPOINTMENTS_DAY_SATURDAY"),
    ];
    const dayName = days[date.getDay()];
    return `${t("APPOINTMENTS_DAY_DATE")} ${dayName}, ${date.getDate()}/${
      date.getMonth() + 1
    }/${date.getFullYear()}`;
  };

  const showStats = !isFullscreen || showFiltersInFullscreen;

  return (
    <div>
      {/* Statistics - hidden in fullscreen when filters are collapsed */}
      {showStats && <AppointmentStats stats={stats} viewType="daily" />}

      {/* Calendar Section */}
      <div className={styles.calendarSection}>
        <div className={styles.calendarHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <h2 className={styles.calendarTitle}>{formatDate(selectedDate)}</h2>
            <IconsLegend />
          </div>
          <div className={styles.calendarNav}>
            <button
              className={styles.navBtn}
              onClick={() => handleDateChange("next")}
              aria-label="Next"
            >
              ‹
            </button>
            <input
              type="date"
              className={styles.datePicker}
              value={tempSelectedDate || toDateStringLocal(selectedDate)}
              onChange={(e) => {
                setTempSelectedDate(e.target.value);
              }}
            />
            {tempSelectedDate && (
              <button
                className={styles.selectDateBtn}
                onClick={() => {
                  const newDate = parseDateStringLocal(tempSelectedDate);
                  setSelectedDate(newDate);
                  setTempSelectedDate(null);
                  // Ensure the selected month is loaded
                  ensureMonthLoaded(newDate);
                }}
              >
                {t("APPOINTMENTS_SELECT_DATE")}
              </button>
            )}
            <button
              className={styles.todayBtn}
              onClick={() => {
                setSelectedDate(new Date());
                setTempSelectedDate(null);
              }}
            >
              {t("APPOINTMENTS_TODAY")}
            </button>
            <span className={styles.currentDate}>
              {selectedDate.toLocaleDateString(getLocale(), {
                month: "long",
                year: "numeric",
              })}
            </span>
            <button
              className={styles.navBtn}
              onClick={() => handleDateChange("prev")}
              aria-label="Previous"
            >
              ›
            </button>
            <ExportTableButtons
              disabled={filteredAppointments.length === 0}
              {...exportProps}
            />
          </div>
        </div>

        {/* Daily Therapist Grid */}
        {isLoading ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>⏳</div>
            <p className={styles.emptyText}>{t("LOADING")}</p>
          </div>
        ) : displayedTherapists.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>👨‍⚕️</div>
            <p className={styles.emptyText}>
              {t("APPOINTMENTS_NO_APPOINTMENTS_FOR_DAY") || "אין מטפלים פעילים"}
            </p>
          </div>
        ) : (
          <div className={styles.dailyScrollContainer}>
            <div
              className={styles.dailyTherapistGrid}
              style={{
                gridTemplateColumns: `auto repeat(${isColumnSplit ? displayedTherapists.length : 1}, minmax(200px, 1fr))`,
              }}
            >
              {/* Corner cell with collapse/expand all button */}
              <div
                className={`${styles.dailyTimeHeaderCorner} ${styles.stickyCorner}`}
              >
                <button
                  className={styles.toggleAllBtn}
                  onClick={toggleAllHours}
                  title={
                    hourlySlots.every((s) => expandedHours[s.split(":")[0]])
                      ? "Collapse All"
                      : "Expand All"
                  }
                >
                  {hourlySlots.every((s) => expandedHours[s.split(":")[0]]) ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="18 15 12 9 6 15"></polyline>
                      <polyline points="18 21 12 15 6 21"></polyline>
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                      <polyline points="6 3 12 9 18 3"></polyline>
                    </svg>
                  )}
                </button>
              </div>
              {isColumnSplit ? (
                displayedTherapists.map((therapist) => (
                  <div
                    key={therapist.id}
                    className={styles.dailyTherapistHeader}
                  >
                    <span>{therapist.name}</span>
                    {/* Device columns carry the room as a quiet second line; therapist columns
                        never set a subtitle, so nothing changes for them. */}
                    {therapist.subtitle && (
                      <span className={styles.dailyColumnSubtitle}>{therapist.subtitle}</span>
                    )}
                    {therapist.id !== -1 && (
                      <div className={styles.therapistHoursContainer}>
                        {getTherapistHours(selectedDate, therapist.id).map(
                          (hourStr, idx) => (
                            <span
                              key={idx}
                              className={styles.therapistHourBadge}
                            >
                              {hourStr}
                            </span>
                          ),
                        )}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className={styles.dailyTherapistHeader}>
                  <span>
                    {t("APPOINTMENTS_ALL_THERAPISTS") || "כל המטפלים"}
                  </span>
                </div>
              )}

              {/* Time Slots and Appointments */}
              {hourlySlots.map((hourSlot) => {
                const [hourStr] = hourSlot.split(":");
                const isExpanded = expandedHours[hourStr];
                const subSlots = isExpanded ? getSubSlots(hourStr) : [];

                const renderSlot = (tId?: number) => {
                  // Proportional view for filtered therapist mode
                  if (isColumnSplit && tId !== undefined) {
                    const isVirtual = tId === -1;
                    const isRTL = document.documentElement.dir === "rtl";
                    const positions =
                      appointmentLayouts.get(tId)?.get(hourSlot) || [];
                    const slotAppts = filteredAppointments.filter((apt) => {
                      if (isVirtual) {
                        const isGoogleEvent = apt.source === "google_calendar";
                        const isOther =
                          (!apt.patientName &&
                            !apt.treatmentGroupName &&
                            !apt.mediaId) ||
                          isGoogleEvent;
                        return (
                          isOther &&
                          apt.therapistsId === Number(admin.id) &&
                          getContainingHourSlot(apt.time || "07:00") ===
                            hourSlot
                        );
                      }
                      return (
                        matchesColumn(apt, tId) &&
                        getContainingHourSlot(apt.time || "07:00") === hourSlot
                      );
                    });
                    return (
                      <div
                        key={tId ? `${tId}-${hourSlot}` : `all-${hourSlot}`}
                        className={getSlotClassName(hourSlot, tId)}
                        style={{
                          cursor: "default",
                          minHeight: 70,
                          padding: 0,
                          overflow: "visible",
                          ...(!isVirtual &&
                          isSlotAvailable(selectedDate, hourSlot, tId)
                            ? { backgroundColor: "rgba(107, 70, 193, 0.1)" }
                            : {}),
                        }}
                        onDragOver={
                          isVirtual
                            ? undefined
                            : (e) => handleDragOver(e, tId, hourSlot)
                        }
                        onDragLeave={isVirtual ? undefined : handleDragLeave}
                        onDrop={
                          isVirtual
                            ? undefined
                            : (e) => handleDrop(e, tId, hourSlot)
                        }
                      >
                        <button
                          className={styles.addAppointmentBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenCreateModal(
                              hourSlot,
                              isVirtual ? undefined : tId,
                            );
                          }}
                        >
                          +
                        </button>
                        {slotAppts.map((apt) => {
                          const fundingColor = getAppointmentColors(apt);
                          const layout = positions.find(
                            (p) => p.appointmentId === apt.id,
                          );
                          if (!layout) return null;
                          const widthPct = 100 / layout.totalColumns;
                          const leftPct = layout.columnIndex * widthPct;
                          const vOffset = calculateVerticalOffset(
                            apt.time || "07:00",
                            hourSlot,
                            isExpanded,
                          );
                          const aptH = calculateHeight(
                            apt.time || "07:00",
                            apt.duration || 45,
                            expandedHours,
                          );
                          return (
                            <div
                              key={apt.id}
                              className={`${styles.dailyAppointment} ${styles.dailyAppointmentProportional}`}
                              style={{
                                borderRightColor: fundingColor.borderColor,
                                backgroundColor: fundingColor.backgroundColor,
                                top: vOffset,
                                height: aptH,
                                left: isRTL ? "auto" : `${leftPct}%`,
                                right: isRTL ? `${leftPct}%` : "auto",
                                width: `calc(${widthPct}% - 4px)`,
                              }}
                              draggable={!isVirtual && !isDeviceMode}
                              onDragStart={
                                !isVirtual
                                  ? (e) =>
                                      handleDragStart(e, apt, tId, hourSlot)
                                  : undefined
                              }
                              onDragEnd={handleDragEnd}
                              onMouseEnter={(e) => {
                                const r =
                                  e.currentTarget.getBoundingClientRect();
                                setTooltip({
                                  text: getAppointmentTooltip(apt),
                                  x: r.left + r.width / 2,
                                  y: r.top,
                                });
                              }}
                              onMouseLeave={() => setTooltip(null)}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAppointmentClick(apt);
                              }}
                            >
                              <AppointmentBadges
  attendanceStatusId={apt.attendanceStatusId}
  hasActionReport={apt.hasActionReport}
  hasTherapistAnswered={apt.hasTherapistAnswered}
  size={12}
  style={{ position: "absolute", top: 2, insetInlineEnd: 2, zIndex: 1, display: "flex", alignItems: "center", gap: 2, }}
  as="div"
/>
                              <div
                                className={styles.dailyAppointmentPatient}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 3,
                                }}
                              >
                                {!apt.patientName && apt.treatmentGroupName
                                  ? truncateText(apt.treatmentGroupName)
                                  : truncateText(
                                      apt.patientName || apt.comments,
                                    )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  }

                  // Default stacked view
                  const slotAppointments = getAppointmentsForSlot(
                    hourSlot,
                    tId,
                  );
                  const displayedAppointments = slotAppointments.slice(0, 5);
                  const hasMore = slotAppointments.length > 5;
                  return (
                    <div
                      key={tId ? `${tId}-${hourSlot}` : `all-${hourSlot}`}
                      className={getSlotClassName(hourSlot, tId)}
                      style={{ cursor: "default" }}
                      onDragOver={(e) => handleDragOver(e, tId || 0, hourSlot)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, tId || 0, hourSlot)}
                    >
                      <button
                        className={styles.addAppointmentBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenCreateModal(hourSlot, tId);
                        }}
                      >
                        +
                      </button>
                      {displayedAppointments.map((apt) => {
                        const fundingColor = getAppointmentColors(apt);
                        return (
                          <div
                            key={apt.id}
                            className={styles.dailyAppointment}
                            style={{
                              borderRightColor: fundingColor.borderColor,
                              backgroundColor: fundingColor.backgroundColor,
                            }}
                            // Not draggable in device mode: moving a patient between machines is a
                            // different action from moving them between therapists, and dragging
                            // would bypass the device availability check entirely.
                            draggable={!isDeviceMode}
                            onDragStart={(e) =>
                              handleDragStart(
                                e,
                                apt,
                                tId || apt.therapistsId || 0,
                                hourSlot,
                              )
                            }
                            onDragEnd={handleDragEnd}
                            onMouseEnter={(e) => {
                              const r = e.currentTarget.getBoundingClientRect();
                              setTooltip({
                                text: getAppointmentTooltip(apt),
                                x: r.left + r.width / 2,
                                y: r.top,
                              });
                            }}
                            onMouseLeave={() => setTooltip(null)}
                            onClick={() => handleAppointmentClick(apt)}
                          >
                            <AppointmentBadges
  attendanceStatusId={apt.attendanceStatusId}
  hasActionReport={apt.hasActionReport}
  hasTherapistAnswered={apt.hasTherapistAnswered}
  size={12}
  style={{ position: "absolute", top: 2, insetInlineEnd: 2, zIndex: 1, display: "flex", alignItems: "center", gap: 2, }}
  as="div"
/>
                            <div
                              className={styles.dailyAppointmentPatient}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 3,
                              }}
                            >
                              {!apt.patientName && apt.treatmentGroupName
                                ? truncateText(apt.treatmentGroupName)
                                : truncateText(apt.patientName || apt.comments)}
                              {!isColumnSplit &&
                                getAppointmentOwnerName(apt) && (
                                  <span
                                    style={{
                                      fontSize: "9px",
                                      opacity: 0.7,
                                      marginRight: "4px",
                                    }}
                                  >
                                    ({getAppointmentOwnerName(apt)})
                                  </span>
                                )}
                            </div>
                          </div>
                        );
                      })}
                      {hasMore && (
                        <div
                          className={styles.moreAppointments}
                          onClick={(e) =>
                            handleMoreAppointmentsClick(
                              e,
                              slotAppointments,
                              tId,
                            )
                          }
                        >
                          +{slotAppointments.length - 5}{" "}
                          {t("APPOINTMENTS_MORE")}
                        </div>
                      )}
                    </div>
                  );
                };

                return (
                  <React.Fragment key={hourSlot}>
                    {/* Main Hour Row */}
                    <div
                      className={`${styles.dailyTimeHeader} ${styles.timeHeaderHour}`}
                    >
                      <button
                        onClick={() => toggleHour(hourStr)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: "4px",
                          marginInlineEnd: "4px",
                          fontSize: "12px",
                        }}
                        title={isExpanded ? "Collapse" : "Expand"}
                      >
                        {isExpanded ? "▲" : "▼"}
                      </button>
                      {hourSlot}
                    </div>
                    {isColumnSplit
                      ? displayedTherapists.map((therapist) =>
                          renderSlot(therapist.id),
                        )
                      : renderSlot()}

                    {/* Sub-Slots (15, 30, 45) - Only if expanded */}
                    {subSlots.map((subSlot) => (
                      <React.Fragment key={subSlot}>
                        <div
                          className={`${styles.dailyTimeHeader} ${styles.timeHeaderQuarter}`}
                        >
                          {subSlot}
                        </div>
                        {isColumnSplit ? (
                          // In proportional filter view, sub-slots are empty grid lines.
                          // Appointments are already shown proportionally in the main hour slot
                          // and visually overflow into the sub-slot space via overflow:visible.
                          displayedTherapists.map((therapist) => {
                            const isVirtual = therapist.id === -1;
                            const slotIsAvailable =
                              !isVirtual &&
                              isSlotAvailable(
                                selectedDate,
                                subSlot,
                                therapist.id,
                              );
                            return (
                              <div
                                key={`${therapist.id}-${subSlot}`}
                                className={getSlotClassName(
                                  subSlot,
                                  therapist.id,
                                )}
                                style={{
                                  cursor: "default",
                                  padding: 0,
                                  minHeight: 35,
                                  ...(slotIsAvailable
                                    ? {
                                        backgroundColor:
                                          "rgba(107, 70, 193, 0.1)",
                                      }
                                    : {}),
                                }}
                                onDragOver={
                                  isVirtual
                                    ? undefined
                                    : (e) =>
                                        handleDragOver(e, therapist.id, subSlot)
                                }
                                onDragLeave={
                                  isVirtual ? undefined : handleDragLeave
                                }
                                onDrop={
                                  isVirtual
                                    ? undefined
                                    : (e) =>
                                        handleDrop(e, therapist.id, subSlot)
                                }
                              >
                                <button
                                  className={styles.addAppointmentBtn}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenCreateModal(
                                      subSlot,
                                      isVirtual ? undefined : therapist.id,
                                    );
                                  }}
                                >
                                  +
                                </button>
                              </div>
                            );
                          })
                        ) : (
                          <div
                            className={getSlotClassName(subSlot)}
                            onDragOver={(e) => handleDragOver(e, 0, subSlot)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, 0, subSlot)}
                          >
                            <button
                              className={styles.addAppointmentBtn}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenCreateModal(subSlot);
                              }}
                            >
                              +
                            </button>
                            {/* Render sub-slot appointments in default view */}
                            {getAppointmentsForSlot(subSlot).map((apt) => {
                              const fundingColor = getAppointmentColors(apt);
                              return (
                                <div
                                  key={apt.id}
                                  className={styles.dailyAppointment}
                                  style={{
                                    borderRightColor: fundingColor.borderColor,
                                    backgroundColor:
                                      fundingColor.backgroundColor,
                                  }}
                                  onMouseEnter={(e) => {
                                    const r =
                                      e.currentTarget.getBoundingClientRect();
                                    setTooltip({
                                      text: getAppointmentTooltip(apt),
                                      x: r.left + r.width / 2,
                                      y: r.top,
                                    });
                                  }}
                                  onMouseLeave={() => setTooltip(null)}
                                  onClick={() => handleAppointmentClick(apt)}
                                >
                                  <AppointmentBadges
  attendanceStatusId={apt.attendanceStatusId}
  hasActionReport={apt.hasActionReport}
  hasTherapistAnswered={apt.hasTherapistAnswered}
  size={12}
  style={{ position: "absolute", top: 2, insetInlineEnd: 2, zIndex: 1, display: "flex", alignItems: "center", gap: 2, }}
  as="div"
/>
                                  <div
                                    className={styles.dailyAppointmentPatient}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 3,
                                    }}
                                  >
                                    {!apt.patientName && apt.treatmentGroupName
                                      ? apt.treatmentGroupName
                                      : apt.patientName || apt.comments}
                                    {getAppointmentOwnerName(apt) &&
                                      (apt.patientName ||
                                        apt.treatmentGroupName) && (
                                        <span
                                          style={{
                                            fontSize: "9px",
                                            opacity: 0.7,
                                            marginRight: "4px",
                                          }}
                                        >
                                          ({getAppointmentOwnerName(apt)})
                                        </span>
                                      )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Fixed Tooltip - renders above all sticky headers */}
      {tooltip && (
        <div
          className={styles.weeklyTooltip}
          style={{
            top: tooltip.y,
            left: tooltip.x,
            transform: "translateX(-50%) translateY(calc(-100% - 8px))",
          }}
        >
          {tooltip.text}
        </div>
      )}

      {/* Appointment Details Modal */}
      {isModalOpen && selectedAppointment && (
        <AppointmentDetailsModal
          appointment={selectedAppointment}
          onClose={handleCloseModal}
          onAppointmentCancelled={handleAppointmentCancelled}
        />
      )}

      {/* Create Appointment Modal */}
      {showCreateModal && (
        <CreateAppointmentModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
          initialDate={createModalDate}
          initialTime={createModalTime}
          therapistId={createModalTherapistId}
        />
      )}

      {/* Move Confirmation Modal */}
      {showMoveConfirmation && pendingMove && (
        <MoveConfirmationModal
          moveData={{
            appointment: pendingMove.appointment,
            fromDay: {
              date: toDateStringLocal(selectedDate),
              dayName: "",
              dayNumber: selectedDate.getDate(),
              isToday: false,
              isWeekend: false,
            },
            fromTime: pendingMove.fromTime,
            fromEndTime: pendingMove.fromEndTime,
            toDay: {
              date: toDateStringLocal(selectedDate),
              dayName: "",
              dayNumber: selectedDate.getDate(),
              isToday: false,
              isWeekend: false,
            },
            toTime: pendingMove.toTime,
            toEndTime: pendingMove.toEndTime,
            toTherapistId: pendingMove.toTherapistId,
            toTherapistGuid: pendingMove.toTherapistGuid,
            toTherapistName: pendingMove.toTherapistName,
            warning: pendingMove.warning,
          }}
          onConfirm={handleConfirmMove}
          onCancel={handleCancelMove}
        />
      )}

      {/* Toast Notifications */}
      {showToast && (
        <div
          className={`${styles.toast} ${
            toastType === "error" ? styles.toastError : styles.toastSuccess
          }`}
        >
          {toastMessage}
        </div>
      )}

      {/* Day Appointments Modal */}
      {isDayModalOpen && selectedDay && (
        <DayAppointmentsModal
          date={selectedDay.date}
          appointments={selectedDay.appointments}
          onClose={handleCloseDayModal}
          onAppointmentClick={handleDayAppointmentClick}
        />
      )}
    </div>
  );
};

export default DailyView;
